<template>
	<v-form ref="form" v-model="valid">
		<h6 class="text-body-1 font-weight-bold">運送便情報入力</h6>
		<v-row no-gutters>
			<v-col class="p-auto-1" cols="12" sm="6">
				<IncrementalSearchForm
					:value.sync="selectedQuoteRequest"
					:label="'見積番号'"
					:itemText="'quote_number'"
					:targetName="'運送費見積'"
					:keywordKey="'quote_number_cont'"
					:url="'/erp/v1/incremental/logi_quote_requests'"
					:classArray="['mt-4', 'append-outer']"
					:initialItems="initialQuoteRequests"
					returnObject
					requireEmit
					@change="setSelectedQuoteRequest($event)"
				>
					<template v-slot:append-outer>
						<LogiQuoteSearchDialog
							:suppliers="suppliers"
							:car_models="car_models"
							@set-quote-request="setSelectedQuoteRequestBySearch($event)"
						/>
					</template>
				</IncrementalSearchForm>
			</v-col>
			<v-col class="p-auto-1" cols="12" sm="6">
				<v-menu
					ref="menu"
					:close-on-content-click="false"
					transition="scale-transition"
					offset-y
					min-width="auto"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							readonly
							outlined
							dense
							label="手配依頼日（必須）"
							v-model="deliveryOrder.order_date"
							v-bind="attrs"
							v-on="on"
							class="mt-4"
							@click="calendar = true"
							:rules="[rules.required]"
						/>
					</template>
					<v-date-picker
						v-model="deliveryOrder.order_date"
						no-title
						scrollable
						@input="calendar = false"
						locale="ja"
					>
					</v-date-picker>
				</v-menu>
			</v-col>
		</v-row>
		<v-row no-gutters>
			<v-col class="p-auto-1" cols="12" sm="6">
				<v-select
					v-model="deliveryOrder.supplier_id"
					label="運送会社（必須）"
					:items="suppliers"
					item-value="id"
					item-text="name"
					class="mt-4"
					outlined
					dense
					@change="setPicName()"
					:rules="[rules.required]"
				/>
			</v-col>
			<v-col class="p-auto-1" cols="12" sm="6">
				<v-text-field
					v-model="deliveryOrder.pic_name"
					label="担当者（必須）"
					class="mt-4"
					outlined
					dense
					:rules="[rules.required]"
				/>
			</v-col>
			<v-col class="p-auto-1" cols="12" sm="6">
				<v-text-field
					v-model="deliveryOrder.price"
					label="合計金額"
					class="mt-4"
					outlined
					dense
					type="number"
					:hint="`現在入力されている車種と台数/式の合計金額は${totalPrice}円です。`"
					hide-spin-buttons
					:rules="[rules.integer]"
					:suffix="'円'"
				/>
			</v-col>
			<v-col class="p-auto-1" cols="12" sm="6">
				<v-select
					v-model="deliveryOrder.shipping_place_id"
					label="集荷場所（必須）"
					:items="shipping_places"
					item-value="id"
					item-text="name"
					class="mt-4"
					outlined
					dense
					:rules="[rules.required]"
				/>
			</v-col>
			<v-col class="p-auto-1" cols="12" sm="6">
				<v-textarea label="備考" v-model="deliveryOrder.memo" class="mt-4" dense outlined />
			</v-col>
			<v-col class="p-auto-1" cols="12" sm="6">
				<v-checkbox
					class="mt-4"
					v-model="deliveryOrder.send_by_fax"
					:value="true"
					label="FAXで送る"
				/>
			</v-col>
		</v-row>
		<v-row no-gutters align="center">
			<h6 class="text-body-1 font-weight-bold mr-4">車種情報入力</h6>
			<v-btn color="secondary" @click="addDeliverOrderCar()">
				<v-icon class="mr-1">mdi-plus</v-icon>
				車種追加
			</v-btn>
		</v-row>
		<div>
			<div class="mt-4 top-divider" v-for="(car, i) in filterCarAttributes" :key="i">
				<v-row no-gutters>
					<h6 class="text-body-1 mt-2">車種 {{ i + 1 }}</h6>
					<v-btn v-if="filterCarAttributes.length > 1" icon @click="deleteCarAttribute(car)"
						><v-icon>mdi-delete</v-icon></v-btn
					>
				</v-row>
				<v-row no-gutters>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-select
							v-model="car.car_model"
							label="車種（必須）"
							:items="car_models"
							class="mt-4"
							outlined
							dense
							:rules="[rules.required]"
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field
							v-model="car.number_of_cars"
							label="台数/式（必須）"
							class="mt-4"
							outlined
							dense
							type="number"
							hide-spin-buttons
							:rules="[rules.integer, rules.required]"
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field
							v-model="car.price"
							label="単価"
							class="mt-4"
							outlined
							dense
							type="number"
							hide-spin-buttons
							:rules="[rules.integer]"
							:suffix="'円'"
						/>
					</v-col>
				</v-row>
			</div>
		</div>
		<v-divider class="my-4" />
		<v-row no-gutters align="center">
			<h6 class="text-body-1 font-weight-bold mr-4">納入先別運送製品入力</h6>
			<OrderIndividualInfoSelectDialog @set-infos="setDeliveryOrderIndividualInfos($event)" />
		</v-row>
		<div>
			<div v-for="(info, i) in infosByFullAddress" :key="i" class="top-divider">
				<div>
					<h6 class="text-body-1 mt-2">納入先{{ i + 1 }}</h6>
					<div v-if="info.infos.length">
						<v-chip
							class="mt-2 mr-2"
							v-for="orderIndividualInfo in info.infos"
							:key="`${i}-${orderIndividualInfo.id}`"
						>
							{{ common.productNameWithProductNumber(orderIndividualInfo) }}
						</v-chip>
					</div>
					<div class="text-body-2" v-else>適当製品なし</div>
				</div>
				<v-row no-gutters>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field
							class="mt-4"
							v-model="info.delivery_info.zipcode"
							label="郵便番号（必須）"
							outlined
							dense
							:rules="[rules.required]"
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-select
							class="mt-4"
							v-model="info.delivery_info.prefecture"
							label="都道府県（必須）"
							:items="$store.state.prefectures"
							outlined
							dense
							:rules="[rules.required]"
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field
							class="mt-4"
							v-model="info.delivery_info.city"
							label="市郡町村（必須）"
							outlined
							dense
							:rules="[rules.required]"
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-textarea
							class="mt-4"
							v-model="info.delivery_info.address"
							rows="1"
							auto-grow
							label="住所（必須）"
							outlined
							dense
							:rules="[rules.required]"
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field
							class="mt-4"
							v-model="info.delivery_info.company_name"
							label="会社名（必須）"
							outlined
							dense
							:rules="[rules.required]"
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field
							class="mt-4"
							v-model="info.delivery_info.person_name"
							label="客先担当者名（必須）"
							outlined
							dense
							:rules="[rules.required]"
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field
							class="mt-4"
							v-model="info.delivery_info.phone"
							label="客先連絡先 電話番号（必須）"
							outlined
							dense
							:rules="[rules.required]"
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field
							class="mt-4"
							v-model="info.delivery_info.fax"
							label="客先連絡先 FAX"
							outlined
							dense
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-menu
							ref="menu"
							:close-on-content-click="false"
							transition="scale-transition"
							offset-y
							min-width="auto"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									readonly
									outlined
									dense
									label="客先納入日（必須）"
									v-model="info.delivery_info.deadline"
									v-bind="attrs"
									v-on="on"
									class="mt-4"
									:rules="[rules.required]"
								/>
							</template>
							<v-date-picker
								v-model="info.delivery_info.deadline"
								no-title
								scrollable
								locale="ja"
							/>
						</v-menu>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field
							class="mt-4"
							v-model="info.delivery_info.deadline_time"
							label="客先納入時間"
							outlined
							dense
							type="time"
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-menu
							ref="menu"
							:close-on-content-click="false"
							transition="scale-transition"
							offset-y
							min-width="auto"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									readonly
									outlined
									dense
									label="集荷日（必須）"
									v-model="info.delivery_info.shipping_date"
									v-bind="attrs"
									v-on="on"
									class="mt-4"
									:rules="[rules.required]"
								/>
							</template>
							<v-date-picker
								v-model="info.delivery_info.shipping_date"
								no-title
								scrollable
								locale="ja"
							/>
						</v-menu>
					</v-col>
				</v-row>
			</div>
		</div>
		<v-row class="mt-4" justify="center" no-gutters>
			<v-col cols="12" sm="6" lg="4">
				<v-row justify="space-between" no-gutters>
					<v-btn color="secondary" @click="openOrderDialog()" :disabled="!valid">
						<v-icon class="mr-1">mdi-plus</v-icon>
						配車依頼書作成
					</v-btn>
					<v-btn color="secondary" @click="$emit('submit-form', 'draft')" :disabled="!valid">
						<v-icon class="mr-1">mdi-pencil-box-outline</v-icon>
						下書き保存
					</v-btn>
				</v-row>
			</v-col>
		</v-row>

		<DeliveryOrderConfirmationDialog
			:dialog.sync="orderDialog"
			:send_by_fax="deliveryOrder.send_by_fax"
			@create-order="$emit('submit-form', $event)"
		/>
	</v-form>
</template>

<script>
import common from "../../../helper/common"
import rules from "../../../helper/rules"
import IncrementalSearchForm from "../../shared/IncrementalSearchForm.vue"
import LogiQuoteSearchDialog from "./LogiQuoteSearchDialog.vue"
import OrderIndividualInfoSelectDialog from "./OrderIndividualInfoSelectDialog.vue"
import DeliveryOrderConfirmationDialog from "./DeliveryOrderConfirmationDialog.vue"

export default {
	props: {
		delivery_order: {
			type: Object,
			required: true,
			default: () => {
				return {}
			},
		},
		infos_by_full_address: {
			type: Array,
			required: true,
			default: () => {
				return []
			},
		},
		suppliers: {
			type: Array,
			required: true,
			default: () => {
				return []
			},
		},
		shipping_places: {
			type: Array,
			required: true,
			default: () => {
				return []
			},
		},
		car_models: {
			type: Array,
			required: true,
			default: () => {
				return []
			},
		},
		selected_quote_request: {
			type: null,
			required: false,
		},
	},
	components: {
		IncrementalSearchForm,
		LogiQuoteSearchDialog,
		OrderIndividualInfoSelectDialog,
		DeliveryOrderConfirmationDialog,
	},
	data() {
		return {
			orderDialog: false,
			selectedQuoteRequest: null,
			initialQuoteRequests: [],
			valid: false,
			rules,
			common,
		}
	},
	mounted() {
		if (this.selected_quote_request) {
			this.selectedQuoteRequest = this.selected_quote_request
			this.$set(this, "initialQuoteRequests", [this.selected_quote_request])
		}
	},
	methods: {
		setSelectedQuoteRequest(quoteRequest) {
			if (quoteRequest) {
				this.deliveryOrder.quote_request_id = quoteRequest.id
				this.deliveryOrder.supplier_id = quoteRequest.supplier.id
				this.deliveryOrder.pic_name = quoteRequest.supplier.pic_name
				this.deliveryOrder.price = quoteRequest.logi_quote.price
				this.deliveryOrder.shipping_place_id = quoteRequest.logi_quote.shipping_place_id
				this.resetCarAttributes()
				this.deliveryOrder.delivery_order_cars_attributes =
					this.deliveryOrder.delivery_order_cars_attributes.concat(
						quoteRequest.logi_quote.logi_quote_cars.map((c) => ({
							car_model: c.car_model,
							number_of_cars: c.number_of_cars,
							price: c.price,
						}))
					)
			} else {
				this.deliveryOrder.quote_request_id = null
				this.deliveryOrder.supplier_id = null
				this.deliveryOrder.pic_name = null
				this.deliveryOrder.price = null
				this.deliveryOrder.shipping_place_id = null
				this.resetCarAttributes()
				this.deliveryOrder.delivery_order_cars_attributes =
					this.deliveryOrder.delivery_order_cars_attributes.concat({
						car_model: null,
						number_of_cars: null,
						price: null,
					})
			}
		},
		setSelectedQuoteRequestBySearch(quoteRequest) {
			this.selectedQuoteRequest = quoteRequest
			this.setSelectedQuoteRequest(quoteRequest)
			this.$set(this, "initialQuoteRequests", [quoteRequest])
		},
		setPicName() {
			const supplier = this.suppliers.find((s) => s.id == this.deliveryOrder.supplier_id)
			if (supplier) {
				this.deliveryOrder.pic_name = supplier.pic_name
			}
		},
		addDeliverOrderCar() {
			this.deliveryOrder.delivery_order_cars_attributes.push({
				price: null,
				number_of_cars: null,
				car_model: null,
			})
		},
		openOrderDialog() {
			this.orderDialog = true
		},
		setDeliveryOrderIndividualInfos(orderIndividualInfos) {
			const deletedDeliveryOrderIndividualInfos = this.infosByFullAddress
				.map((info) =>
					info.delivery_order_individual_infos ? info.delivery_order_individual_infos : []
				)
				.flat()
			if (deletedDeliveryOrderIndividualInfos.length) {
				const deletedAttributes = deletedDeliveryOrderIndividualInfos.map(
					(deletedDeliveryOrderIndividualInfo) => ({
						id: deletedDeliveryOrderIndividualInfo.id,
						_destroy: true,
					})
				)
				this.$emit("set-deleted-attributes", deletedAttributes)
			}
			this.infosByFullAddress = orderIndividualInfos.reduce((groups, current) => {
				const group = groups.find(
					(group) => group.full_address === current.delivery_info.full_address
				)
				if (group) {
					group.infos.push(current)
				} else {
					groups.push({
						full_address: current.delivery_info.full_address,
						delivery_info: {
							zipcode: current.delivery_info.zipcode,
							prefecture: current.delivery_info.prefecture,
							city: current.delivery_info.city,
							address: current.delivery_info.address,
							fax: current.delivery_info.fax,
							phone: current.delivery_info.phone,
							company_name: current.delivery_info.company_name,
							person_name: current.delivery_info.person_name,
							deadline: current.delivery_info.deadline,
							deadline_time: "",
							shipping_date: current.delivery_info.shipping_date,
						},
						infos: [current],
					})
				}
				return groups
			}, [])
		},
		resetCarAttributes() {
			this.deliveryOrder.delivery_order_cars_attributes =
				this.deliveryOrder.delivery_order_cars_attributes
					.filter((c) => c.id)
					.map((c) => ({ id: c.id, _destroy: true }))
		},
		deleteCarAttribute(car) {
			const index = this.deliveryOrder.delivery_order_cars_attributes.findIndex((c) => c == car)
			if (index == -1) return
			if (car.idd) {
				this.deliveryOrder.delivery_order_cars_attributes[index] = {
					id: car.id,
					_destroy: true,
				}
			} else {
				this.deliveryOrder.delivery_order_cars_attributes.splice(index, 1)
			}
		},
	},
	computed: {
		filterCarAttributes() {
			return this.deliveryOrder.delivery_order_cars_attributes?.filter((c) => !c._destroy)
		},
		totalPrice() {
			return this.filterCarAttributes?.reduce((total, current) => {
				return total + current.price * current.number_of_cars
			}, 0)
		},
		deliveryOrder: {
			get() {
				return this.delivery_order
			},
			set(val) {
				return this.$emit("update:delivery_order", val)
			},
		},
		infosByFullAddress: {
			get() {
				return this.infos_by_full_address
			},
			set(val) {
				return this.$emit("update:infos_by_full_address", val)
			},
		},
	},
	watch: {
		selected_quote_request: {
			handler(val) {
				this.selectedQuoteRequest = val
				this.initialQuoteRequests = [val]
			},
			deep: true,
		},
	},
}
</script>